import React, { createContext, useContext, useState, ReactNode } from 'react';

interface GlobalToast {
  id: string;
  title: string;
  description: string;
  variant: 'success' | 'info' | 'destructive';
  persistent?: boolean;
}

interface GlobalToastContextType {
  toast: GlobalToast | null;
  showGlobalToast: (toast: Omit<GlobalToast, 'id'>) => void;
  hideGlobalToast: () => void;
}

const GlobalToastContext = createContext<GlobalToastContextType | undefined>(undefined);

export const useGlobalToast = () => {
  const context = useContext(GlobalToastContext);
  if (!context) {
    throw new Error('useGlobalToast must be used within a GlobalToastProvider');
  }
  return context;
};

interface GlobalToastProviderProps {
  children: ReactNode;
}

export const GlobalToastProvider: React.FC<GlobalToastProviderProps> = ({ children }) => {
  const [toast, setToast] = useState<GlobalToast | null>(null);

  const showGlobalToast = (toastData: Omit<GlobalToast, 'id'>) => {
    const newToast: GlobalToast = {
      ...toastData,
      id: Date.now().toString(),
    };
    setToast(newToast);

    // Auto hide after duration unless persistent
    if (!toastData.persistent) {
      const duration = toastData.variant === 'destructive' ? 8000 : 5000;
      setTimeout(() => {
        setToast(null);
      }, duration);
    }
  };

  const hideGlobalToast = () => {
    setToast(null);
  };

  return (
    <GlobalToastContext.Provider value={{ toast, showGlobalToast, hideGlobalToast }}>
      {children}
    </GlobalToastContext.Provider>
  );
};
